import { useState, useEffect } from 'react';
import { devicesAPI, tenantsAPI, storesAPI } from '../services/api';
import { Cpu, Wifi, WifiOff, RefreshCw, Trash2, Clock, QrCode, X } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import toast from 'react-hot-toast';

function StatusBadge({ device }) {
  const last = device.last_heartbeat_at ? new Date(device.last_heartbeat_at) : null;
  const minutesAgo = last ? Math.floor((Date.now() - last) / 60000) : null;
  const isOnline = minutesAgo !== null && minutesAgo < 2;

  return (
    <span className={`badge badge-${isOnline ? 'green' : 'grey'}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {isOnline ? <Wifi size={10} /> : <WifiOff size={10} />}
      {isOnline ? 'Online' : minutesAgo === null ? 'Never seen' : `${minutesAgo}m ago`}
    </span>
  );
}

export default function Devices() {
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState('');
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    tenantsAPI.list().then(r => setTenants(r.data || []));
  }, []);

  useEffect(() => {
    if (!selectedTenant) return;
    storesAPI.list(selectedTenant).then(r => {
      const s = r.data || [];
      setStores(s);
      if (s.length > 0) setSelectedStore(s[0].id);
    });
  }, [selectedTenant]);

  useEffect(() => {
    if (!selectedStore) return;
    loadDevices();
  }, [selectedStore]);

  const loadDevices = async () => {
    setLoading(true);
    try {
      const res = await devicesAPI.listByStore(selectedStore);
      setDevices(res.data || []);
    } catch { toast.error('Failed to load devices'); }
    finally { setLoading(false); }
  };

  const refresh = async () => {
    setRefreshing(true);
    await loadDevices();
    setRefreshing(false);
  };

  const decommission = async (id, name) => {
    if (!window.confirm(`Remove device "${name}"?`)) return;
    try {
      await devicesAPI.decommission(id);
      toast.success('Device removed');
      setDevices(prev => prev.filter(d => d.id !== id));
      loadDevices();
    } catch { toast.error('Failed'); }
  };

  const formatTime = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' });
  };

  const currentStore = stores.find(s => s.id === selectedStore);
  const onlineCount = devices.filter(d => {
    if (!d.last_heartbeat_at) return false;
    return (Date.now() - new Date(d.last_heartbeat_at)) < 120000;
  }).length;


  const QRModal = ({ store }) => (
    <div className="modal-overlay" onClick={() => setShowQR(false)}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 360, textAlign: 'center' }}>
        <div className="modal-header">
          <h2 className="modal-title">Pair a Device</h2>
          <button className="modal-close" onClick={() => setShowQR(false)}>✕</button>
        </div>
        <div style={{ padding: '24px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
          <div style={{ background: '#fff', padding: 16, borderRadius: 12 }}>
            <QRCodeSVG value={store.pairing_code} size={200} bgColor="#ffffff" fgColor="#0a0a0f" level="H" />
          </div>
          <div>
            <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: 6, color: 'var(--accent-bright)', marginBottom: 8 }}>{store.pairing_code}</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Scan with the InStore Player app</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>or enter the code manually</div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Devices</h1>
          <p className="page-subtitle">Manage paired store players and monitor their status</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <select className="form-select" style={{ width: 180 }} value={selectedTenant} onChange={e => { setSelectedTenant(e.target.value); setSelectedStore(''); setDevices([]); }}>
            <option value="">Select brand...</option>
            {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          {stores.length > 0 && (
            <select className="form-select" style={{ width: 160 }} value={selectedStore} onChange={e => setSelectedStore(e.target.value)}>
              {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          {selectedStore && (
            <button className="btn btn-ghost btn-sm" onClick={refresh} disabled={refreshing}>
              <RefreshCw size={14} className={refreshing ? 'spin' : ''} /> Refresh
            </button>
          )}
        </div>
      </div>

      {showQR && currentStore && <QRModal store={currentStore} />}
      {!selectedTenant ? (
        <div className="card">
          <div className="empty-state"><Cpu size={48} /><h3>Select a brand</h3><p>Choose a brand and store to view its devices</p></div>
        </div>
      ) : !selectedStore ? (
        <div className="card">
          <div className="empty-state"><Cpu size={48} /><h3>Select a store</h3><p>Choose a store to view its devices</p></div>
        </div>
      ) : (
        <>
          {/* Stats */}
          <div className="grid-3" style={{ marginBottom: 20 }}>
            <div className="card" style={{ padding: 20 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>TOTAL DEVICES</div>
              <div style={{ fontSize: 28, fontWeight: 800 }}>{devices.length}</div>
            </div>
            <div className="card" style={{ padding: 20 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>ONLINE NOW</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--green)' }}>{onlineCount}</div>
            </div>
            <div className="card" style={{ padding: 20 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>PAIRING CODE</div>
              <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: 3, color: 'var(--accent-bright)' }}>
                {currentStore?.pairing_code || '—'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Enter this in the player app</div>
              <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={() => setShowQR(true)}><QrCode size={13} /> Show QR Code</button>
            </div>
          </div>

          {/* Devices table */}
          <div className="card">
            {loading ? (
              <div className="loading-state"><div className="spinner" /></div>
            ) : devices.length === 0 ? (
              <div className="empty-state">
                <Cpu size={48} />
                <h3>No devices paired</h3>
                <p>Open the InStore Player app and enter pairing code <strong style={{ color: 'var(--accent-bright)' }}>{currentStore?.pairing_code}</strong></p>
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Device</th>
                      <th>Status</th>
                      <th>Hardware ID</th>
                      <th>IP Address</th>
                      <th>Last Seen</th>
                      <th>Paired</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devices.map(device => (
                      <tr key={device.id}>
                        <td>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{device.name || 'Player'}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{device.device_type || 'player'}</div>
                        </td>
                        <td><StatusBadge device={device} /></td>
                        <td style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{device.hardware_id?.slice(0, 20) || '—'}</td>
                        <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{device.last_ip || '—'}</td>
                        <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Clock size={11} />{formatTime(device.last_heartbeat_at)}
                          </div>
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{formatTime(device.created_at)}</td>
                        <td>
                          <button className="btn btn-danger btn-sm" onClick={() => decommission(device.id, device.name || 'this device')} title="Remove device">
                            <Trash2 size={11} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
